import React, { useState, useEffect } from 'react';
import { Button } from '@fluentui/react-components';
import { ChevronLeft24Regular, ChevronRight24Regular } from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import './styles.scss';
import { formatToLocalTime } from '#root/services/utils';

interface DateNavigatorProps {
  initialDate?: Date;
  onDateChange?: (date: Date) => void;
  maxDate?: Date;
  minDate?: Date;
}

const DateNavigator: React.FC<DateNavigatorProps> = ({
  initialDate = new Date(),
  onDateChange,
  maxDate,
  minDate
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  useEffect(() => {
    setSelectedDate(initialDate);
  }, [initialDate]);

  const isMinDate = (date: Date): boolean => {
    if (!minDate) return false;
    
    // Compare dates properly by setting time to midnight
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    
    const minDateToCheck = new Date(minDate);
    minDateToCheck.setHours(0, 0, 0, 0);
    
    // Check if the date is less than or equal to minDate
    return dateToCheck.getTime() <= minDateToCheck.getTime();
  };

  const isMaxDate = (date: Date): boolean => {
    if (!maxDate) return false;
    
    // Compare dates properly by setting time to midnight
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    
    const maxDateToCheck = new Date(maxDate);
    maxDateToCheck.setHours(0, 0, 0, 0);
    
    // Check if the date is greater than or equal to maxDate
    return dateToCheck.getTime() >= maxDateToCheck.getTime();
  };

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    
    // Prevent selecting dates before minDate
    if (minDate) {
      const minDateToCheck = new Date(minDate);
      minDateToCheck.setHours(0, 0, 0, 0);
      
      const newDateToCheck = new Date(newDate);
      newDateToCheck.setHours(0, 0, 0, 0);
      
      if (newDateToCheck.getTime() < minDateToCheck.getTime()) {
        return;
      }
    }
    
    setSelectedDate(newDate);
    onDateChange?.(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    
    // Prevent selecting dates beyond maxDate
    if (maxDate) {
      const maxDateToCheck = new Date(maxDate);
      maxDateToCheck.setHours(0, 0, 0, 0);
      
      const newDateToCheck = new Date(newDate);
      newDateToCheck.setHours(0, 0, 0, 0);
      
      if (newDateToCheck.getTime() > maxDateToCheck.getTime()) {
        return;
      }
    }
    
    setSelectedDate(newDate);
    onDateChange?.(newDate);
  };

  const handleDatePickerChange = (date: Date | null | undefined) => {
    if (date) {
      // Prevent selecting dates beyond today or before minDate
      if (maxDate && date > maxDate || (minDate && date < minDate)) {
        return;
      }
      
      setSelectedDate(date);
      onDateChange?.(date);
    }
  };

  const formatDate = (date: Date | null | undefined): string => {
    return date ? formatToLocalTime(date, 'date') : '';
  };

  return (
    <div className="date-navigator">
      <Button
        icon={<ChevronLeft24Regular />}
        appearance="secondary"
        onClick={handlePreviousDay}
        aria-label="Previous day"
        disabled={minDate !== undefined && isMinDate(selectedDate)}
      />
      <DatePicker 
        value={selectedDate}
        onSelectDate={handleDatePickerChange}
        className="date-navigator__date-picker"
        formatDate={formatDate}
        placeholder="MM/DD/YYYY"
        maxDate={maxDate}
        minDate={minDate}
      />
      <Button
        icon={<ChevronRight24Regular />}
        appearance="secondary"
        onClick={handleNextDay}
        aria-label="Next day"
        disabled={maxDate !== undefined && isMaxDate(selectedDate)}
      />
    </div>
  );
};

export default DateNavigator;
